/**
 * Modal Component
 * 
 * Standardized modal dialogs for consistent UI across the application.
 * Supports different sizes, accessibility, and animations.
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './Modal.css';

/**
 * Modal sizes enum
 */
export const MODAL_SIZES = {
  SMALL: 'small',     // 400px
  MEDIUM: 'medium',   // 540px
  LARGE: 'large',     // 720px
  XLARGE: 'xlarge',   // 900px
  FULL: 'full'        // Full screen
};

/**
 * Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Close handler
 * @param {string} props.size - Modal size
 * @param {boolean} props.closeOnOverlay - Close when clicking overlay
 * @param {boolean} props.closeOnEsc - Close on Escape key
 * @param {boolean} props.showCloseButton - Show close button
 * @param {string} props.className - Additional CSS class
 * @param {React.ReactNode} props.children - Modal content
 */
export function Modal({
  isOpen,
  onClose,
  size = MODAL_SIZES.MEDIUM,
  closeOnOverlay = true,
  closeOnEsc = true,
  showCloseButton = true,
  className = '',
  children,
  ...props
}) {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (closeOnEsc && e.key === 'Escape') {
      onClose();
    }
  }, [closeOnEsc, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Focus management and body scroll lock
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = 'hidden';
      
      // Focus the modal
      setTimeout(() => {
        modalRef.current?.focus();
      }, 10);

      // Add escape key listener
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleKeyDown);
        
        // Restore focus
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div 
        ref={modalRef}
        className={`modal-container modal-${size} ${className}`}
        tabIndex={-1}
        {...props}
      >
        {showCloseButton && (
          <button 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}

/**
 * Modal Header
 */
export function ModalHeader({
  title,
  subtitle,
  icon,
  className = '',
  children
}) {
  return (
    <div className={`modal-header ${className}`}>
      {icon && (
        <div className="modal-header-icon">
          <FontAwesomeIcon icon={icon} />
        </div>
      )}
      <div className="modal-header-text">
        {title && <h2 className="modal-title">{title}</h2>}
        {subtitle && <p className="modal-subtitle">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

/**
 * Modal Body
 */
export function ModalBody({ className = '', children, ...props }) {
  return (
    <div className={`modal-body ${className}`} {...props}>
      {children}
    </div>
  );
}

/**
 * Modal Footer
 */
export function ModalFooter({ 
  className = '', 
  align = 'right',
  children,
  ...props 
}) {
  return (
    <div className={`modal-footer modal-footer-${align} ${className}`} {...props}>
      {children}
    </div>
  );
}

/**
 * Confirmation Modal - Pre-configured modal for confirmations
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary', // 'primary', 'danger', 'success'
  loading = false,
  icon,
  children
}) {
  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={MODAL_SIZES.SMALL}>
      <ModalHeader title={title} icon={icon} />
      <ModalBody>
        {message && <p className="confirm-modal-message">{message}</p>}
        {children}
      </ModalBody>
      <ModalFooter>
        <button 
          className="modal-btn modal-btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          {cancelLabel}
        </button>
        <button 
          className={`modal-btn modal-btn-${confirmVariant}`}
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? 'Processing...' : confirmLabel}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Alert Modal - Pre-configured modal for alerts
 */
export function AlertModal({
  isOpen,
  onClose,
  type = 'info', // 'info', 'success', 'warning', 'error'
  title,
  message,
  buttonLabel = 'OK',
  icon,
  children
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size={MODAL_SIZES.SMALL}>
      <ModalHeader title={title} icon={icon}>
        <div className={`alert-modal-indicator alert-modal-${type}`} />
      </ModalHeader>
      <ModalBody>
        {message && <p className="alert-modal-message">{message}</p>}
        {children}
      </ModalBody>
      <ModalFooter align="center">
        <button className="modal-btn modal-btn-primary" onClick={onClose}>
          {buttonLabel}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Form Modal - Pre-configured modal for forms
 */
export function FormModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  subtitle,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  loading = false,
  submitDisabled = false,
  size = MODAL_SIZES.MEDIUM,
  icon,
  children
}) {
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onSubmit) {
      await onSubmit(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size}>
      <form onSubmit={handleSubmit}>
        <ModalHeader title={title} subtitle={subtitle} icon={icon} />
        <ModalBody>
          {children}
        </ModalBody>
        <ModalFooter>
          <button 
            type="button"
            className="modal-btn modal-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button 
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading || submitDisabled}
          >
            {loading ? 'Saving...' : submitLabel}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default Modal;
